import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { Loader2, AlertCircle } from "lucide-react";
import Layout from "../components/Layout";
import { supabase, isSupabaseConfigured } from "../lib/supabaseClient";

/**
 * Unified Supabase auth callback handler.
 * Detects the `type` param (signup | recovery | magiclink | email_change | invite)
 * and routes to the appropriate page while preserving the auth payload.
 *
 * Supabase email templates can point to:
 *   /auth/callback?type=signup
 *   /auth/callback?type=recovery
 *   /auth/callback (default — will inspect the URL)
 */
export default function AuthCallback() {
  const nav = useNavigate();
  const loc = useLocation();
  const [params] = useSearchParams();
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!isSupabaseConfigured) {
      setErr("Autenticação não configurada.");
      return;
    }

    const searchType = params.get("type");

    // Parse hash params too (Supabase implicit flow sends tokens in #...)
    const hashRaw = window.location.hash || "";
    const hashParams = new URLSearchParams(hashRaw.startsWith("#") ? hashRaw.slice(1) : hashRaw);
    const hashType = hashParams.get("type");
    const hashError = hashParams.get("error_description") || hashParams.get("error");

    const type = searchType || hashType;

    if (hashError) {
      setErr(decodeURIComponent(hashError));
      return;
    }

    // Recovery flow → password reset page (preserving the hash so it can set session)
    if (type === "recovery") {
      nav("/redefinir-password" + window.location.hash, { replace: true });
      return;
    }

    // Signup / email confirmation flow — wait for supabase-js to establish session
    // (it consumes the hash automatically via detectSessionInUrl).
    let cancelled = false;
    const finalize = async () => {
      // Give supabase-js time to detect the token
      await new Promise((r) => setTimeout(r, 250));
      const { data } = await supabase.auth.getSession();
      if (cancelled) return;
      if (data?.session) {
        // Clean URL hash and go to dashboard
        window.history.replaceState({}, document.title, "/meus-casos");
        nav("/meus-casos", { replace: true });
      } else {
        // No session — go to login as a safe fallback
        nav("/entrar", { replace: true });
      }
    };

    // Also listen for the imminent SIGNED_IN event
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (cancelled) return;
      if ((event === "SIGNED_IN" || event === "INITIAL_SESSION") && session) {
        cancelled = true;
        sub?.subscription?.unsubscribe?.();
        window.history.replaceState({}, document.title, "/meus-casos");
        nav("/meus-casos", { replace: true });
      }
    });

    finalize();
    return () => {
      cancelled = true;
      sub?.subscription?.unsubscribe?.();
    };
  }, [nav, loc, params]);

  return (
    <Layout>
      <section className="min-h-[60vh] flex items-center py-20">
        <div className="max-w-md mx-auto px-6 w-full text-center">
          <div className="dossier p-10">
            {err ? (
              <>
                <AlertCircle className="w-10 h-10 text-blood mx-auto mb-4" />
                <h2 className="headline text-2xl mb-3">Não conseguimos validar</h2>
                <p className="text-bone-muted text-sm leading-relaxed">{err}</p>
              </>
            ) : (
              <>
                <Loader2 className="w-10 h-10 text-blood mx-auto mb-5 animate-spin" />
                <p className="mono text-[11px] tracking-[0.3em] text-bone-muted">A validar acesso...</p>
              </>
            )}
          </div>
        </div>
      </section>
    </Layout>
  );
}
